// src/components/ProgressTracker.tsx

import {
  BookOutlined,
  BoxPlotOutlined,
  BranchesOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HighlightOutlined,
  LoadingOutlined,
  NodeExpandOutlined,
  OrderedListOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  StarOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Spin,
  Statistic,
  Steps,
  Tooltip,
  Typography,
} from "antd";
import { motion } from "framer-motion";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { notify } from "@/components/Notification";
import useAppStore from "@/store/useAppStore";
import type { ProgressTrackerProps } from "@/types";

const { Title, Text } = Typography;

// Defines a single stage in the step-by-step progress UI
interface StageDefinition {
  title: string;
  status: string;
  icon: React.ReactNode;
  isCompleted?: boolean;
  isSkipped?: boolean;
}

// Maps raw job statuses to a canonical stage type (used to find current step)
const statusMapping: { [key: string]: string } = {
  initializing: "initializing",
  extracting_text: "extracting_text",
  text_extracted: "extracting_text",
  optimizing: "optimizing",
  optimization_completed: "optimizing",
  generating_multiple_choice_quiz: "generating_multiple_choice_quiz",
  multiple_choice_quiz_completed: "generating_multiple_choice_quiz",
  generating_short_answer_quiz: "generating_short_answer_quiz",
  short_answer_quiz_completed: "generating_short_answer_quiz",
  generating_anki_flashcards: "generating_anki_flashcards",
  anki_flashcards_completed: "generating_anki_flashcards",
  generating_presentation_slides: "generating_presentation_slides",
  presentation_slides_completed: "generating_presentation_slides",
  generating_summary: "generating_summary",
  summary_completed: "generating_summary",
  generating_mindmap: "generating_mindmap",
  mindmap_completed: "generating_mindmap",
  generating_readability_analysis: "generating_readability_analysis",
  readability_analysis_completed: "generating_readability_analysis",
  generating_lesson_plan: "generating_lesson_plan",
  lesson_plan_completed: "generating_lesson_plan",
  packaging_results: "packaging_results",
  completed: "completed",
};

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  onJobComplete,
  generate_multiple_choice_quiz,
  generate_short_answer_quiz,
  generate_anki_flashcards,
  generate_presentation_slides,
  generate_summary,
  generate_mindmap,
  generate_readability_analysis,
  generate_lesson_plan,
  isAdditionalGeneration = false,
  existingJobStatus,
  originalJobData,
}) => {
  // Extract needed store values and actions
  const { jobStatus, currentJobId, currentRequestId, forceRefreshJobStatus, error } = useAppStore();

  // Tracks which stages have already been completed (for additional generations)
  const completedStages = useMemo(() => {
    if (!isAdditionalGeneration || !originalJobData) {
      return [];
    }

    const completed: string[] = [];
    if (originalJobData.generate_multiple_choice_quiz) completed.push("multiple_choice_quiz");
    if (originalJobData.generate_short_answer_quiz) completed.push("short_answer_quiz");
    if (originalJobData.generate_anki_flashcards) completed.push("anki_flashcards");
    if (originalJobData.generate_presentation_slides) completed.push("presentation_slides");
    if (originalJobData.generate_summary) completed.push("summary");
    if (originalJobData.generate_mindmap) completed.push("mindmap");
    if (originalJobData.generate_readability_analysis) completed.push("readability_analysis");
    if (originalJobData.generate_lesson_plan) completed.push("lesson_plan");
    return completed;
  }, [isAdditionalGeneration, originalJobData]);

  // Controls a brief initialization loading spinner to prevent UI flicker
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // When mounted, scroll this section into view for user convenience
    const progressSection = document.querySelector(".progress-tracker-section");
    if (progressSection) {
      progressSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    // Short delay to show a spinner before progress displays, avoids flicker
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Handle job completion
  useEffect(() => {
    if (jobStatus?.status === "completed" && onJobComplete && currentJobId) {
      onJobComplete(currentJobId);
    }
  }, [jobStatus, onJobComplete, currentJobId]);

  const stages: StageDefinition[] = useMemo(() => {
    const shouldAddStage = (flag: boolean, stageType: string) => {
      if (!isAdditionalGeneration) return flag;
      return flag && !completedStages.includes(stageType);
    };

    const baseStages: StageDefinition[] = isAdditionalGeneration
      ? []
      : [
          {
            title: "Initializing",
            status: "initializing",
            icon: <BuildOutlined />,
            isCompleted: isAdditionalGeneration,
          },
          {
            title: "Extracting Text",
            status: "extracting_text",
            icon: <FileSearchOutlined />,
            isCompleted: isAdditionalGeneration,
          },
          {
            title: "Optimizing",
            status: "optimizing",
            icon: <HighlightOutlined />,
            isCompleted: isAdditionalGeneration,
          },
        ];

    if (shouldAddStage(generate_multiple_choice_quiz, "multiple_choice_quiz")) {
      baseStages.push({
        title: "Generating Multiple Choice Quiz",
        status: "generating_multiple_choice_quiz",
        icon: <OrderedListOutlined />,
        isCompleted: completedStages.includes("multiple_choice_quiz"),
      });
    }

    if (shouldAddStage(generate_short_answer_quiz, "short_answer_quiz")) {
      baseStages.push({
        title: "Generating Short Answer Quiz",
        status: "generating_short_answer_quiz",
        icon: <QuestionCircleOutlined />,
        isCompleted: completedStages.includes("short_answer_quiz"),
      });
    }

    if (shouldAddStage(generate_anki_flashcards, "anki_flashcards")) {
      baseStages.push({
        title: "Generating Anki Flashcards",
        status: "generating_anki_flashcards",
        icon: <ProjectOutlined />,
        isCompleted: completedStages.includes("anki_flashcards"),
      });
    }

    if (shouldAddStage(generate_presentation_slides, "presentation_slides")) {
      baseStages.push({
        title: "Generating Presentation Slides",
        status: "generating_presentation_slides",
        icon: <BoxPlotOutlined />,
        isCompleted: completedStages.includes("presentation_slides"),
      });
    }

    if (shouldAddStage(generate_summary, "summary")) {
      baseStages.push({
        title: "Generating Summary",
        status: "generating_summary",
        icon: <ReadOutlined />,
        isCompleted: completedStages.includes("summary"),
      });
    }

    if (shouldAddStage(generate_mindmap, "mindmap")) {
      baseStages.push({
        title: "Generating Mindmap",
        status: "generating_mindmap",
        icon: <BranchesOutlined />,
        isCompleted: completedStages.includes("mindmap"),
      });
    }

    if (shouldAddStage(generate_readability_analysis, "readability_analysis")) {
      baseStages.push({
        title: "Generating Readability Analysis",
        status: "generating_readability_analysis",
        icon: <FileProtectOutlined />,
        isCompleted: completedStages.includes("readability_analysis"),
      });
    }

    if (shouldAddStage(generate_lesson_plan, "lesson_plan")) {
      baseStages.push({
        title: "Generating Lesson Plan",
        status: "generating_lesson_plan",
        icon: <BookOutlined />,
        isCompleted: completedStages.includes("lesson_plan"),
      });
    }

    baseStages.push(
      {
        title: "Packaging Results",
        status: "packaging_results",
        icon: <NodeExpandOutlined />,
      },
      {
        title: "Completed",
        status: "completed",
        icon: <CheckCircleOutlined />,
      },
    );

    return baseStages;
  }, [
    isAdditionalGeneration,
    completedStages,
    generate_multiple_choice_quiz,
    generate_short_answer_quiz,
    generate_anki_flashcards,
    generate_presentation_slides,
    generate_summary,
    generate_mindmap,
    generate_readability_analysis,
    generate_lesson_plan,
  ]);

  const getCurrentStageIndex = () => {
    if (!jobStatus) return 0;

    const currentStage = statusMapping[jobStatus.status] || jobStatus.status;
    const stageIndex = stages.findIndex((stage) => stage.status === currentStage);
    return stageIndex >= 0 ? stageIndex : 0;
  };

  const inputTokenTooltip = useMemo(
    () => (
      <Tooltip
        title="This is the total number of 'tokens' (similar to words, used by the AI internally) that were sent to the AI to communicate both your original raw text and all the instructions required to turn it into the final optimized document (and additional outputs if you generated them). The token counts are shown for information purposes only."
        mouseEnterDelay={0.5}
      >
        <div className="cursor-help">
          {" "}
          {/* Add this wrapper div */}
          <Statistic
            title={
              <span className="text-gray-700 font-semibold text-sm sm:text-base">Input Tokens</span>
            }
            value={jobStatus?.total_input_tokens ?? undefined}
            prefix={<FileTextOutlined />}
          />
        </div>
      </Tooltip>
    ),
    [jobStatus?.total_input_tokens],
  );

  const outputTokenTooltip = useMemo(
    () => (
      <Tooltip
        title="This is the total number of 'tokens' (similar to words, used by the AI internally) that were generated by the AI to produce the final optimized document (and additional outputs if you generated them), as well as all intermediate steps required to get there. The token counts are shown for information purposes only."
        mouseEnterDelay={0.5}
      >
        <div className="cursor-help">
          {" "}
          {/* Add this wrapper div */}
          <Statistic
            title={
              <span className="text-gray-700 font-semibold text-sm sm:text-base">
                Output Tokens
              </span>
            }
            value={jobStatus?.total_output_tokens ?? undefined}
            prefix={<FileTextOutlined />}
          />
        </div>
      </Tooltip>
    ),
    [jobStatus?.total_output_tokens],
  );

  if (!jobStatus) {
    return (
      <Card className="mt-4 shadow-lg rounded-lg bg-white">
        <Alert message="No status information available" type="warning" showIcon />
      </Card>
    );
  }

  const currentStageIndex = getCurrentStageIndex();

  const handleCopyDebugInfo = async () => {
    if (!currentJobId || !jobStatus) return;

    const payload = {
      job_id: currentJobId,
      request_id: currentRequestId,
      status: jobStatus.status,
      progress_percentage: jobStatus.progress_percentage,
      updated_at: jobStatus.updated_at?.toISOString?.() ?? null,
      correlation_id: currentRequestId,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      notify.success({ content: "Copied debug info to clipboard." });
    } catch {
      notify.error({ content: "Failed to copy debug info." });
    }
  };

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spin size="small" />
        <span className="ml-2">Initializing progress tracking...</span>
      </div>
    );
  }

  return (
    <motion.div
      className="progress-tracker-section relative z-10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="mt-4 shadow-lg rounded-lg bg-white">
        <Title level={4} className="mb-4 text-center">
          {isAdditionalGeneration ? "Generating Additional Documents" : "Processing Progress"}
        </Title>

        <div className="mb-6">
          <Progress
            percent={jobStatus.progress_percentage}
            status={
              jobStatus.status === "completed"
                ? "success"
                : jobStatus.status === "processing_failed" ||
                    jobStatus.status.endsWith("_failed") ||
                    jobStatus.status.includes("generation_failed")
                  ? "exception"
                  : "active"
            }
            strokeColor={{
              "0%": "#108ee9",
              "100%": "#87d068",
            }}
            size="default"
          />
        </div>

        <Steps
          current={currentStageIndex}
          direction="vertical"
          className="mb-6 custom-steps"
          size="small"
          items={stages.map((stage, index) => ({
            key: stage.title,
            title: <span className="text-sm sm:text-base">{stage.title}</span>,
            description: (
              <span className="text-xs sm:text-sm">
                {jobStatus.status === stage.status ? jobStatus.status_description : ""}
              </span>
            ),
            icon: stage.isCompleted ? (
              <CheckCircleOutlined className="text-green-500" />
            ) : index < currentStageIndex ? (
              <CheckCircleOutlined className="text-green-500" />
            ) : index === currentStageIndex ? (
              jobStatus.status === "completed" ? (
                <CheckCircleOutlined className="text-green-500" />
              ) : (
                <LoadingOutlined className="text-blue-500" />
              )
            ) : (
              stage.icon
            ),
          }))}
        />

        <Collapse
          ghost
          className="mb-2"
          items={[
            {
              key: "advanced",
              label: "Advanced details",
              children: (
                <div>
                  <Row gutter={[16, 16]} className="mb-4">
                    {jobStatus.optimization_quality_score !== null &&
                      jobStatus.optimization_quality_score !== undefined && (
                        <Col xs={24} sm={12}>
                          <Tooltip
                            title="The quality score is automatically determined by comparing the original raw text to the final optimized document; it measures how well the optimized document reflects the original content in terms of accuracy and meaning, while improving and polishing the language and formatting."
                            mouseEnterDelay={0.5}
                          >
                            <Statistic
                              title={
                                <span className="text-gray-700 font-semibold text-sm sm:text-base">
                                  Quality Score
                                </span>
                              }
                              value={jobStatus.optimization_quality_score.toFixed(2)}
                              prefix={<StarOutlined />}
                              suffix="/ 100"
                            />
                          </Tooltip>
                        </Col>
                      )}
                  </Row>

                  <Row gutter={[16, 16]} className="mb-4">
                    {jobStatus.total_input_tokens !== null &&
                      jobStatus.total_input_tokens !== undefined && (
                        <Col xs={24} sm={12}>
                          {inputTokenTooltip}
                        </Col>
                      )}
                    {jobStatus.total_output_tokens !== null &&
                      jobStatus.total_output_tokens !== undefined && (
                        <Col xs={24} sm={12}>
                          {outputTokenTooltip}
                        </Col>
                      )}
                  </Row>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                    {jobStatus.last_processed && (
                      <div>
                        <Text strong>Last Processed:</Text>
                        <br />
                        <Text>{new Date(jobStatus.last_processed).toLocaleString()}</Text>
                      </div>
                    )}
                    {jobStatus.last_optimized && (
                      <div>
                        <Text strong>Last Optimized:</Text>
                        <br />
                        <Text>{new Date(jobStatus.last_optimized).toLocaleString()}</Text>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Button size="small" icon={<CopyOutlined />} onClick={handleCopyDebugInfo}>
                      Copy debug info
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />
        {error && (
          <Alert
            message={error}
            description={jobStatus.error_message}
            type="error"
            showIcon
            className="mt-4"
          />
        )}

        <div className="mt-4 flex justify-end">
          <Tooltip title="Force refresh status" mouseEnterDelay={0.5}>
            <Button
              icon={<SyncOutlined />}
              onClick={forceRefreshJobStatus}
              loading={jobStatus?.status === "checking"}
            >
              Refresh Status
            </Button>
          </Tooltip>
        </div>
      </Card>
    </motion.div>
  );
};

export default ProgressTracker;
